import classnames from 'classnames';
import { ErrorTooltipArrow } from 'components/shared/WithErrorTooltip/ErrorTooltipArrow/ErrorTooltipArrow';
import { ErrorTooltipBody } from 'components/shared/WithErrorTooltip/ErrorTooltipBody/ErrorTooltipBody';
import { ErrorTooltipFooter } from 'components/shared/WithErrorTooltip/ErrorTooltipFooter/ErrorTooltipFooter';
import { ErrorTooltipHeader } from 'components/shared/WithErrorTooltip/ErrorTooltipHeader/ErrorTooltipHeader';
import { useToggle } from 'hooks/useToggle';
import type { IntlKey } from 'locales';
import { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import type { ApiError } from 'utils/http/apiErrorHelper';

export type WithErrorTooltipPosition = 'right' | 'bottom' | 'left' | 'top';
export type WithErrorTooltipSeverity = 'error' | 'warning';
export type WithErrorTooltipColor = 'danger' | 'warning';

export interface WithTechnicalErrorTooltipProps {
  children: ReactNode;
  canCopy?: boolean;
  className?: string;
  contentToCopy?: string;
  e2e?: string;
  error?: ApiError;
  onDismiss?(): void;
  position?: WithErrorTooltipPosition;
  title: IntlKey;
  tooltipClassName?: string;
}

export const TooltipDiv = styled.div`
  min-width: 150px;
`;

export const WithErrorTooltipContainer = styled.div`
  &:hover {
    & > .tooltip {
      z-index: 1031;
    }
  }

  & > .tooltip {
    z-index: 1031;
    &.bs-tooltip-top {
      top: 0;
      left: 0;
      transform: translateY(-100%);
    }
    &.bs-tooltip-bottom {
      top: 100%;
    }
    &.bs-tooltip-left {
      top: 0;
      right: 100%;
    }
    &.bs-tooltip-right {
      top: 0;
      right: 0;
      transform: translate(100%, calc(-1 * var(--spacer-1)));
    }
  }

  .tooltip-inner {
    max-width: unset;
  }
`;

export const WithTechnicalErrorTooltip: FC<WithTechnicalErrorTooltipProps> = ({
  canCopy,
  children,
  className,
  contentToCopy,
  e2e,
  error,
  onDismiss,
  position = 'bottom',
  title,
  tooltipClassName,
}) => {
  const { formatMessage } = useIntl();
  const [isFirstTime, setIsFirstTime] = useState<boolean>(true);
  const { isOn: isTooltipOn, toggleOn: toggleTooltipOn, toggleOff: toggleTooltipOff } = useToggle(true);

  const messages = useMemo(
    () => (error ? [formatMessage({ id: error.message as IntlKey }, error.values)] : []),
    [error, formatMessage],
  );

  const closeTooltip = useCallback(() => {
    toggleTooltipOff();
    if (isFirstTime) {
      setIsFirstTime(false);
    }
  }, [toggleTooltipOff, isFirstTime]);
  const onPointerLeft = useCallback(() => {
    if (!isFirstTime) {
      closeTooltip();
    }
  }, [closeTooltip, isFirstTime]);

  const onDismissed = useCallback(() => {
    if (onDismiss) {
      onDismiss();
    }
    closeTooltip();
  }, [onDismiss, closeTooltip]);

  return (
    <WithErrorTooltipContainer
      className={classnames('with-error-tooltip-component position-relative d-inline-block', className)}
      data-e2e={e2e && `${e2e}-container`}
      onPointerEnter={toggleTooltipOn}
      onPointerLeave={onPointerLeft}
    >
      {children}

      {isTooltipOn && (
        <TooltipDiv
          role="tooltip"
          className={classnames(`tooltip tooltip-danger bs-tooltip-${position}`, 'fade show', tooltipClassName)}
          data-e2e="with-error-tooltip"
        >
          <ErrorTooltipArrow position={position} />

          <div className={classnames('tooltip-inner fs-6 shadow px-0 pb-0', `border border-danger`)} data-e2e="tooltip">
            <ErrorTooltipHeader canCopy={canCopy} content={contentToCopy} title={title} />

            <div className="bg-lvl1">
              <ErrorTooltipBody messages={messages} />

              <ErrorTooltipFooter onDismiss={onDismissed} shouldShowDetails={false} />
            </div>
          </div>
        </TooltipDiv>
      )}
    </WithErrorTooltipContainer>
  );
};

WithTechnicalErrorTooltip.displayName = 'WithErrorTooltip';
